import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, useAction, useApi } from '../lib/api';
import { date, money, titleCase } from '../lib/format';
import { AccountSelect, useSubsidiaries } from '../lib/lookups';
import { useSession } from '../lib/session';
import type { Contract, Customer, Invoice, Payment, Vendor } from '../lib/types';
import { ArchiveButton, ArchivedBadge, ShowArchived } from '../components/archive';
import { Icons } from '../components/icons';
import { Button, ButtonLink, Card, Empty, ErrorNote, Field, Input, Modal, PageHeader, Query, Select, Stat, StatusBadge, Table, Td, Th, Tr } from '../components/ui';
import { InvoiceStatus, isOverdue, RecordPaymentModal } from './Invoices';

export function CustomersPage() {
  const navigate = useNavigate();
  const { can } = useSession();
  const subs = useSubsidiaries();
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const q = useApi<Customer[]>(`/api/customers${search ? `?search=${encodeURIComponent(search)}` : ''}`);
  const archived = q.data?.filter((c) => c.archivedAt).length ?? 0;
  return (
    <>
      <PageHeader
        title="Customers"
        description="Balances are open receivables in each customer's billing currency."
        actions={
          <>
            <ShowArchived count={archived} value={showArchived} onChange={setShowArchived} />
            <div className="relative w-56">
              <span className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-faint">
                <Icons.Search size={15} />
              </span>
              <Input className="pl-8" placeholder="Search customers" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            {can('ACCOUNTANT') && (
              <Button variant="primary" icon={<Icons.Plus size={15} />} onClick={() => setCreating(true)}>
                New customer
              </Button>
            )}
          </>
        }
      />
      <Card pad={false}>
        <Query q={q}>
          {(rows) =>
            rows.length === 0 ? (
              <Empty title={search ? 'No customers match' : 'No customers yet'}>
                {!search && 'Add the companies you bill. Each one keeps its own currency and payment terms.'}
              </Empty>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Name</Th>
                    <Th>Entity</Th>
                    <Th>Currency</Th>
                    <Th>Terms</Th>
                    <Th right>Open balance</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows
                    .filter((c) => showArchived || !c.archivedAt)
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((c) => (
                      <Tr key={c.id} onClick={() => navigate(`/ar/customers/${c.id}`)}>
                        <Td>
                          <div className="font-medium">
                            {c.name}
                            <ArchivedBadge at={c.archivedAt} />
                          </div>
                          <div className="text-[12px] text-muted">{c.email}</div>
                        </Td>
                        <Td muted>{subs.byId.get(c.subsidiaryId)?.name}</Td>
                        <Td muted>{c.currency}</Td>
                        <Td muted>Net {c.paymentTermsDays}</Td>
                        <Td right className={c.balance ? 'font-medium' : 'text-faint'}>
                          {money(c.balance ?? 0, c.currency)}
                        </Td>
                      </Tr>
                    ))}
                </tbody>
              </Table>
            )
          }
        </Query>
      </Card>
      <NewPartyModal kind="customer" open={creating} onClose={() => setCreating(false)} onCreated={(id) => navigate(`/ar/customers/${id}`)} />
    </>
  );
}

export function NewPartyModal({ kind, open, onClose, onCreated }: { kind: 'customer' | 'vendor'; open: boolean; onClose: () => void; onCreated: (id: string) => void }) {
  const subs = useSubsidiaries();
  const [f, setF] = useState({ name: '', email: '', subsidiaryId: '', currency: '', paymentTermsDays: '30' });
  const sub = subs.list.find((s) => s.id === f.subsidiaryId) ?? subs.list[0];
  const create = useAction(
    () =>
      api<{ id: string }>(kind === 'customer' ? '/api/customers' : '/api/vendors', {
        body: { name: f.name, email: f.email || undefined, subsidiaryId: sub?.id, currency: f.currency || sub?.currency, paymentTermsDays: Number(f.paymentTermsDays) || 0 },
      }),
    { success: `${kind === 'customer' ? 'Customer' : 'Vendor'} created` },
  );
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`New ${kind}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!f.name.trim()} loading={create.isPending} onClick={() => create.mutate(undefined, { onSuccess: (r) => (onClose(), onCreated(r.id)) })}>
            Create
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="Name" className="col-span-2">
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        </Field>
        <Field label={kind === 'customer' ? 'Billing email' : 'Remittance email'} className="col-span-2">
          <Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        </Field>
        <Field label="Entity">
          <Select value={sub?.id ?? ''} onChange={(e) => setF({ ...f, subsidiaryId: e.target.value, currency: '' })}>
            {subs.list.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Currency">
          <Select value={f.currency || sub?.currency || 'USD'} onChange={(e) => setF({ ...f, currency: e.target.value })}>
            {['USD', 'GBP', 'EUR', 'INR', 'CAD', 'AUD'].map((c) => (
              <option key={c}>{c}</option>
            ))}
          </Select>
        </Field>
        <Field label="Payment terms (days)">
          <Input inputMode="numeric" value={f.paymentTermsDays} onChange={(e) => setF({ ...f, paymentTermsDays: e.target.value })} />
        </Field>
      </div>
      {create.error && <ErrorNote error={create.error} className="mt-3" />}
    </Modal>
  );
}

/** Name, email and terms; for a vendor also the default expense account and department. Currency and entity stay fixed. */
export function EditPartyModal({ kind, party, onClose }: { kind: 'customer' | 'vendor'; party: Customer | Vendor; onClose: () => void }) {
  const vendor = kind === 'vendor' ? (party as Vendor) : null;
  const [f, setF] = useState({ name: party.name, email: party.email ?? '', paymentTermsDays: String(party.paymentTermsDays), defaultExpenseAccountId: vendor?.defaultExpenseAccountId ?? '', department: vendor?.department ?? '' });
  const save = useAction(
    () =>
      api(`/api/${kind === 'customer' ? 'customers' : 'vendors'}/${party.id}`, {
        method: 'PATCH',
        body: {
          name: f.name,
          email: f.email.trim() || null,
          paymentTermsDays: Number(f.paymentTermsDays) || 0,
          ...(vendor && { defaultExpenseAccountId: f.defaultExpenseAccountId || null, department: f.department.trim() || null }),
        },
      }),
    { success: 'Saved' },
  );
  return (
    <Modal
      open
      onClose={onClose}
      title={`Edit ${kind}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!f.name.trim()} loading={save.isPending} onClick={() => save.mutate(undefined, { onSuccess: onClose })}>
            Save
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="Name" className="col-span-2">
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        </Field>
        <Field label={kind === 'customer' ? 'Billing email' : 'Remittance email'} className="col-span-2">
          <Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        </Field>
        <Field label="Payment terms (days)">
          <Input inputMode="numeric" value={f.paymentTermsDays} onChange={(e) => setF({ ...f, paymentTermsDays: e.target.value })} />
        </Field>
        <Field label="Currency" hint="Fixed: invoices and bills keep it">
          <Input value={party.currency} disabled />
        </Field>
        {vendor && (
          <>
            <Field label="Default expense account" className="col-span-2" hint="Fills in new bill lines">
              <AccountSelect value={f.defaultExpenseAccountId} onChange={(id) => setF({ ...f, defaultExpenseAccountId: id })} types={['EXPENSE', 'ASSET']} placeholder="None" />
            </Field>
            <Field label="Department" className="col-span-2">
              <Input value={f.department} onChange={(e) => setF({ ...f, department: e.target.value })} placeholder="e.g. Engineering" />
            </Field>
          </>
        )}
      </div>
      {save.error && <ErrorNote error={save.error} className="mt-3" />}
    </Modal>
  );
}

export function CustomerPage() {
  const { id } = useParams();
  const { can } = useSession();
  const subs = useSubsidiaries();
  const q = useApi<{ customer: Customer; invoices: Invoice[]; contracts: Contract[]; payments: Payment[] }>(`/api/customers/${id}`);
  const [paying, setPaying] = useState(false);
  const [editing, setEditing] = useState(false);
  return (
    <Query q={q}>
      {({ customer: c, invoices, contracts, payments }) => {
        const open = invoices.filter((i) => i.status === 'ISSUED' || i.status === 'PARTIALLY_PAID');
        const openBal = open.reduce((s, i) => s + i.total - i.amountPaid, 0);
        const overdue = open.filter(isOverdue).reduce((s, i) => s + i.total - i.amountPaid, 0);
        const lifetime = invoices.filter((i) => i.status !== 'DRAFT' && i.status !== 'VOID').reduce((s, i) => s + i.total, 0);
        const active = contracts.filter((x) => x.status === 'ACTIVE');
        return (
          <>
            <PageHeader
              eyebrow={
                <Link to="/ar/customers" className="hover:text-ink">
                  ← Customers
                </Link>
              }
              title={
                <>
                  {c.name}
                  <ArchivedBadge at={c.archivedAt} />
                </>
              }
              description={`${c.email ?? 'No billing email'} · ${subs.byId.get(c.subsidiaryId)?.name} · ${c.currency} · net ${c.paymentTermsDays}`}
              actions={
                can('ACCOUNTANT') && (
                  <>
                    <ArchiveButton path={`/api/customers/${c.id}`} archived={!!c.archivedAt} noun="Customer" />
                    <Button onClick={() => setEditing(true)}>Edit</Button>
                    <ButtonLink to={`/ar/contracts/new?customerId=${c.id}`}>New contract</ButtonLink>
                    <ButtonLink to={`/ar/invoices/new?customerId=${c.id}`}>New invoice</ButtonLink>
                    <Button variant="primary" icon={<Icons.Bank size={15} />} onClick={() => setPaying(true)}>
                      Record payment
                    </Button>
                  </>
                )
              }
            />
            <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-4">
              <Stat label="Open balance" value={money(openBal, c.currency)} sub={`${open.length} open invoices`} />
              <Stat label="Overdue" value={money(overdue, c.currency)} tone={overdue ? 'bad' : 'good'} sub={overdue ? 'Past due date' : 'Nothing overdue'} />
              <Stat label="Billed to date" value={money(lifetime, c.currency, { compact: true })} />
              <Stat label="Active contracts" value={String(active.length)} sub={active.length ? `Through ${date(active.map((x) => x.endDate).sort().pop())}` : 'None active'} />
            </div>
            <div className="grid gap-5 xl:grid-cols-2">
              <Card title="Invoices" pad={false}>
                {invoices.length ? (
                  <Table dense>
                    <thead>
                      <tr>
                        <Th>Number</Th>
                        <Th>Issued</Th>
                        <Th right>Total</Th>
                        <Th right>Balance</Th>
                        <Th>Status</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices
                        .filter((i) => i.status !== 'DRAFT')
                        .slice(0, 24)
                        .map((i) => (
                          <tr key={i.id}>
                            <Td>
                              <Link to={`/ar/invoices/${i.id}`} className="font-medium whitespace-nowrap text-brand hover:underline">
                                {i.number}
                              </Link>
                            </Td>
                            <Td className="num">{date(i.issueDate)}</Td>
                            <Td right>{money(i.total, i.currency)}</Td>
                            <Td right>{i.status === 'VOID' ? '—' : money(i.total - i.amountPaid, i.currency)}</Td>
                            <Td>
                              <InvoiceStatus i={i} />
                            </Td>
                          </tr>
                        ))}
                    </tbody>
                  </Table>
                ) : (
                  <Empty title="No invoices yet" />
                )}
              </Card>
              <div className="space-y-5">
                <Card title="Contracts" pad={false}>
                  {contracts.length ? (
                    <Table dense>
                      <thead>
                        <tr>
                          <Th>Number</Th>
                          <Th>Term</Th>
                          <Th>Billing</Th>
                          <Th right>Value</Th>
                          <Th>Status</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {contracts.map((x) => (
                          <tr key={x.id}>
                            <Td>
                              <Link to={`/ar/contracts/${x.id}`} className="font-medium whitespace-nowrap text-brand hover:underline">
                                {x.number}
                              </Link>
                            </Td>
                            <Td className="num whitespace-nowrap">
                              {date(x.startDate, { year: false })} – {date(x.endDate)}
                            </Td>
                            <Td muted>{titleCase(x.billingFrequency)}</Td>
                            <Td right>{money(x.totalAmount, x.currency)}</Td>
                            <Td>
                              <StatusBadge status={x.status} />
                            </Td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <Empty title="No contracts" />
                  )}
                </Card>
                <Card title="Payments" pad={false}>
                  {payments.length ? (
                    <Table dense>
                      <thead>
                        <tr>
                          <Th>Date</Th>
                          <Th>Reference</Th>
                          <Th right>Amount</Th>
                          <Th right>Unapplied</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {payments.slice(0, 12).map((p) => (
                          <tr key={p.id}>
                            <Td className="num">{date(p.date)}</Td>
                            <Td muted>
                              {p.journalEntryId ? (
                                <Link to={`/journal/${p.journalEntryId}`} className="hover:text-brand hover:underline">
                                  {p.reference ?? 'Payment'}
                                </Link>
                              ) : (
                                p.reference
                              )}
                            </Td>
                            <Td right>{money(p.amount, p.currency)}</Td>
                            <Td right className={p.unapplied ? 'text-warn' : 'text-faint'}>
                              {money(p.unapplied, p.currency)}
                            </Td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <Empty title="No payments yet" />
                  )}
                </Card>
              </div>
            </div>
            <RecordPaymentModal open={paying} onClose={() => setPaying(false)} customerId={c.id} currency={c.currency} defaultAmount={openBal || undefined} />
            {editing && <EditPartyModal kind="customer" party={c} onClose={() => setEditing(false)} />}
          </>
        );
      }}
    </Query>
  );
}

